import { ExternalLink } from 'lucide-react';
import React from 'react';

import { useAuth } from '@app/auth';
import NotificacionesDropdown from '@modules/avisos/components/NotificacionesDropdown';
import { IconButton } from '@shared/ui';

import Sidebar from './Sidebar';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();

  const getRoleLabel = (role?: string) => {
    if (!role) return 'Usuario';
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };

  // Duplica la vista actual en una pestaña nueva sin cerrar la actual. La
  // sesión vive en sessionStorage, que el navegador COPIA a la pestaña hija solo
  // si se abre con opener: por eso no se pasa 'noopener' (con él, la ventana
  // nueva pedía login). Es nuestra propia URL, así que el opener no expone nada;
  // aun así se corta el enlace en cuanto la copia ya está hecha.
  const abrirEnNuevaVentana = () => {
    const nueva = window.open(window.location.href, '_blank');
    if (nueva) nueva.opener = null;
  };

  return (
    <div className="flex min-h-screen bg-brand-ghost">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-brand-tomato focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Saltar al contenido principal
      </a>
      <Sidebar />

      <main className="flex-1 flex flex-col min-w-0">
        {/* El header crea un stacking context (sticky + z-30); el dropdown
            de la campana es hijo y por tanto siempre queda por encima del
            contenido de la página, incluido el <thead sticky z-10> de
            tablas como DataTable. */}
        <header
          role="banner"
          className="h-16 bg-white border-b border-brand-silver/30 px-6 flex items-center justify-between sticky top-0 z-30"
        >
          <div className="flex items-center lg:ml-0 ml-12" />

          <div className="flex items-center space-x-4">
            <IconButton
              aria-label="Abrir esta vista en una ventana nueva"
              title="Abrir en ventana nueva"
              icon={<ExternalLink size={20} aria-hidden="true" />}
              onClick={abrirEnNuevaVentana}
            />
            <NotificacionesDropdown />
            <div className="h-8 w-px bg-brand-silver/30 mx-2" aria-hidden="true" />
            <div className="flex items-center space-x-3 hover:bg-brand-ghost p-1 pr-3 rounded-full transition-colors">
              <div
                aria-hidden="true"
                className="w-9 h-9 bg-brand-tomato rounded-full flex items-center justify-center text-white font-bold shadow-sm"
              >
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-brand-dark leading-none">
                  {user?.name || 'Usuario'}
                </p>
                <p className="text-xs text-brand-gray mt-1">{getRoleLabel(user?.role)}</p>
              </div>
            </div>
          </div>
        </header>

        <div id="main-content" className="p-6 md:p-8 flex-1" tabIndex={-1}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
